import { Component, OnInit, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { firstValueFrom } from "rxjs";
import {
  WebinarAgendaItem,
  WebinarDeptBenefit,
  WebinarEvent,
  WebinarMetric,
  WebinarRegistration,
  WebinarTranslations,
} from "./webinar.interface";

@Component({
  selector: "app-webinar",
  templateUrl: "./webinar.component.html",
  styleUrls: ["./webinar.component.scss"],
  standalone: false,
})
export class WebinarComponent implements OnInit, OnDestroy {
  submitted = false;
  isSubmitting = false;
  errorMessage = "";
  formTouched = false;

  registration: WebinarRegistration = {
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    team_size: "",
    stack: "",
    consent: false,
  };

  event: WebinarEvent = {
    title: "",
    subtitle: "",
    datetimeISO: "",
    duration: "",
    host: { name: "", role: "" },
    ctaPrimary: "",
    ctaSecondary: "",
    agendaDownloadUrl: "",
  };
  metrics: WebinarMetric[] = [];
  deptBenefits: WebinarDeptBenefit[] = [];
  agendaItems: WebinarAgendaItem[] = [];
  webinarType: "webinar" | "webinar2" = "webinar";

  countdown = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isActive: false,
  };

  gcalHref = "";
  icsHref = "";
  jsonLdScript: HTMLScriptElement | null = null;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.determineWebinarType();
    this.initializeLanguage();
    this.loadTranslations();
    this.initializeIframeCommunication();
  }

  private determineWebinarType(): void {
    const path = this.route.snapshot.url.map((s) => s.path).join("/");
    this.webinarType = path === "webinar2" ? "webinar2" : "webinar";
  }

  private initializeLanguage(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");
    const currentLang =
      langParam && ["pl", "es", "en"].includes(langParam) ? langParam : "pl";
    this.translate.use(currentLang);
  }

  private loadTranslations(): void {
    this.translate
      .get([
        "webinar.event.title",
        "webinar.event.subtitle",
        "webinar.event.duration",
        "webinar.event.hostName",
        "webinar.event.hostRole",
        "webinar.event.ctaPrimary",
        "webinar.event.ctaSecondary",
        "webinar.metrics.avgCtr",
        "webinar.metrics.moreClicks",
        "webinar.metrics.lessItTime",
        "webinar.metrics.monthlyRoi",
        "webinar.value.marketing.title",
        "webinar.value.marketing.point1",
        "webinar.value.marketing.point2",
        "webinar.value.marketing.point3",
        "webinar.value.hr.title",
        "webinar.value.hr.point1",
        "webinar.value.hr.point2",
        "webinar.value.hr.point3",
        "webinar.value.sales.title",
        "webinar.value.sales.point1",
        "webinar.value.sales.point2",
        "webinar.value.sales.point3",
        "webinar.value.it.title",
        "webinar.value.it.point1",
        "webinar.value.it.point2",
        "webinar.value.it.point3",
        "webinar.agenda.item1",
        "webinar.agenda.item2",
        "webinar.agenda.item3",
        "webinar.agenda.item4",
        "webinar.agenda.item5",
        "webinar.agenda.item6",
        "webinar.agenda.item7",
      ])
      .subscribe((translations: WebinarTranslations) => {
        this.event = {
          title: translations["webinar.event.title"] || "",
          subtitle: translations["webinar.event.subtitle"] || "",
          datetimeISO:
            this.webinarType === "webinar2"
              ? "2025-12-11T16:00:00+01:00"
              : "2025-02-05T15:00:00-03:00", // 5 February 2025, 3:00 PM CLT (Santiago de Chile)
          duration: translations["webinar.event.duration"] || "",
          host: {
            name: translations["webinar.event.hostName"] || "",
            role: translations["webinar.event.hostRole"] || "",
          },
          ctaPrimary: translations["webinar.event.ctaPrimary"] || "",
          ctaSecondary: translations["webinar.event.ctaSecondary"] || "",
          agendaDownloadUrl: "/assets/pdf/Webinar_gSignature.pdf",
        };

        this.metrics = [
          {
            label: translations["webinar.metrics.avgCtr"] || "",
            value: "8,84%",
            icon: "chart-line",
          },
          {
            label: translations["webinar.metrics.moreClicks"] || "",
            value: "+184%",
            icon: "mouse-pointer",
          },
          {
            label: translations["webinar.metrics.lessItTime"] || "",
            value: "85%",
            icon: "shield-alt",
          },
          {
            label: translations["webinar.metrics.monthlyRoi"] || "",
            value: "627,51%",
            icon: "chart-bar",
          },
        ];

        this.deptBenefits = [
          {
            title: translations["webinar.value.marketing.title"] || "",
            icon: "bullhorn",
            points: [
              translations["webinar.value.marketing.point1"] || "",
              translations["webinar.value.marketing.point2"] || "",
              translations["webinar.value.marketing.point3"] || "",
            ],
          },
          {
            title: translations["webinar.value.hr.title"] || "",
            icon: "users",
            points: [
              translations["webinar.value.hr.point1"] || "",
              translations["webinar.value.hr.point2"] || "",
              translations["webinar.value.hr.point3"] || "",
            ],
          },
          {
            title: translations["webinar.value.sales.title"] || "",
            icon: "rocket",
            points: [
              translations["webinar.value.sales.point1"] || "",
              translations["webinar.value.sales.point2"] || "",
              translations["webinar.value.sales.point3"] || "",
            ],
          },
          {
            title: translations["webinar.value.it.title"] || "",
            icon: "microchip",
            points: [
              translations["webinar.value.it.point1"] || "",
              translations["webinar.value.it.point2"] || "",
              translations["webinar.value.it.point3"] || "",
            ],
          },
        ];

        this.agendaItems = [
          { t: "0:00-0:05", title: translations["webinar.agenda.item1"] || "" },
          { t: "0:05-0:15", title: translations["webinar.agenda.item2"] || "" },
          { t: "0:15-0:25", title: translations["webinar.agenda.item3"] || "" },
          { t: "0:25-0:35", title: translations["webinar.agenda.item4"] || "" },
          { t: "0:35-0:45", title: translations["webinar.agenda.item5"] || "" },
          { t: "0:45-0:55", title: translations["webinar.agenda.item6"] || "" },
          { t: "0:55-1:00", title: translations["webinar.agenda.item7"] || "" },
        ];

        this.initializeCountdown();
        this.initializeCalendarLinks();
        this.initializeJsonLd();
      });
  }

  private initializeCountdown(): void {
    if (!this.event.datetimeISO) {
      return;
    }

    const target = new Date(this.event.datetimeISO).getTime();
    const updateCountdown = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);

      this.countdown.days = Math.floor(diff / (1000 * 60 * 60 * 24));
      this.countdown.hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      this.countdown.minutes = Math.floor((diff / (1000 * 60)) % 60);
      this.countdown.seconds = Math.floor((diff / 1000) % 60);
      this.countdown.isActive = diff > 0;
    };

    updateCountdown();
    setInterval(updateCountdown, 1000);
  }

  private initializeCalendarLinks(): void {
    if (!this.event.datetimeISO) {
      return;
    }

    const start = new Date(this.event.datetimeISO);
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    const formatDate = (d: Date) =>
      d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const dates = `${formatDate(start)}/${formatDate(end)}`;
    this.gcalHref = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(this.event.title)}&details=${encodeURIComponent("Webinar online. Link do Google Meet przyjdzie mailem po rejestracji.")}&location=${encodeURIComponent("Online (Google Meet)")}&dates=${dates}&ctz=${encodeURIComponent("Europe/Warsaw")}`;

    const toICSDate = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, "0");
      return (
        d.getUTCFullYear().toString() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        "T" +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) +
        "Z"
      );
    };

    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//gSignature//Webinar//PL",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${Date.now()}@gsignature.com`,
      `DTSTAMP:${toICSDate(new Date())}`,
      `SUMMARY:${this.event.title}`,
      `DESCRIPTION:Webinar online. Link do Google Meet przyjdzie mailem po rejestracji.`,
      `DTSTART:${toICSDate(start)}`,
      `DTEND:${toICSDate(end)}`,
      `LOCATION:Online (Google Meet)`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    this.icsHref = URL.createObjectURL(blob);
  }

  private initializeJsonLd(): void {
    if (!this.event.datetimeISO) {
      return;
    }

    const startISO = this.event.datetimeISO;
    const endISO = new Date(
      new Date(this.event.datetimeISO).getTime() + 60 * 60 * 1000,
    ).toISOString();

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: this.event.title,
      description: this.event.subtitle,
      startDate: startISO,
      endDate: endISO,
      isAccessibleForFree: true,
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      organizer: {
        "@type": "Organization",
        name: "gSignature",
        url: "https://gsignature.com",
      },
      location: {
        "@type": "VirtualLocation",
        url: "https://meet.google.com",
        name: "Google Meet",
      },
      performer: {
        "@type": "Person",
        name: this.event.host.name,
        jobTitle: this.event.host.role,
      },
      offers: {
        "@type": "Offer",
        url: "#register",
        price: 0,
        priceCurrency: "PLN",
        availability: "https://schema.org/InStock",
        validFrom: new Date().toISOString(),
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    this.jsonLdScript = script;
  }

  ngOnDestroy(): void {
    if (this.jsonLdScript && this.jsonLdScript.parentNode) {
      this.jsonLdScript.parentNode.removeChild(this.jsonLdScript);
    }
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  getFormattedDate(): string {
    if (!this.event.datetimeISO) {
      return "";
    }
    const date = new Date(this.event.datetimeISO);
    const currentLang = this.translate.currentLang || "pl";
    const localeMap: { [key: string]: string } = {
      pl: "pl-PL",
      en: "en-US",
      es: "es-ES",
    };

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone:
        this.webinarType === "webinar2"
          ? "America/New_York"
          : "America/Santiago", // CLT - Santiago de Chile
      timeZoneName: "short",
    };

    return date.toLocaleString(localeMap[currentLang] || "pl-PL", options);
  }

  async onSubmit(event: Event, _form: unknown): Promise<void> {
    event.preventDefault();
    this.formTouched = true;

    if (!this.isFormValid()) {
      return;
    }

    if (!this.registration.consent) {
      this.translate
        .get("webinar.register.errors.consent")
        .subscribe((msg: string) => {
          this.errorMessage = msg;
        });
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = "";

    try {
      const currentLang = this.translate.currentLang || "pl";
      const registrationData = {
        ...this.registration,
        lang: currentLang,
        webinarType: this.webinarType,
      };
      await firstValueFrom(
        this.http.post("/webinar/register", registrationData),
      );
      this.submitted = true;
    } catch (error: unknown) {
      const errorObj = error as {
        error?: { message?: string };
        message?: string;
      };
      this.translate
        .get("webinar.register.errors.generic")
        .subscribe((msg: string) => {
          this.errorMessage =
            errorObj?.error?.message || errorObj?.message || msg;
        });
      console.error("Registration error:", error);
    } finally {
      this.isSubmitting = false;
    }
  }

  isFormValid(): boolean {
    return !!(
      this.registration.name &&
      this.registration.email &&
      this.registration.company &&
      this.registration.role &&
      this.registration.phone &&
      this.registration.team_size &&
      this.registration.stack
    );
  }

  getFieldError(fieldName: keyof WebinarRegistration): string {
    if (!this.formTouched) {
      return "";
    }

    const value = this.registration[fieldName];
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return this.translate.instant(
        "webinar.register.errors.required",
      ) as string;
    }

    if (
      fieldName === "email" &&
      value &&
      typeof value === "string" &&
      !this.isValidEmail(value)
    ) {
      return this.translate.instant("webinar.register.errors.email") as string;
    }

    if (
      fieldName === "phone" &&
      value &&
      typeof value === "string" &&
      !this.isValidPhone(value)
    ) {
      return this.translate.instant("webinar.register.errors.phone") as string;
    }

    return "";
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isValidPhone(phone: string): boolean {
    return /^\+?[0-9 ()-]{7,}$/.test(phone);
  }

  scrollToSection(sectionId: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 40;
      const isInIframe = window.parent && window.parent !== window;

      if (isInIframe) {
        const elementRect = element.getBoundingClientRect();
        const currentScroll =
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;
        const targetPosition = elementRect.top + currentScroll - headerHeight;

        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: "smooth",
        });

        window.parent.postMessage(
          {
            type: "SCROLL_IFRAME",
            sectionId,
            position: targetPosition,
          },
          "*",
        );
      } else {
        const elementRect = element.getBoundingClientRect();
        const currentScroll =
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;
        const targetPosition = elementRect.top + currentScroll - headerHeight;

        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: "smooth",
        });
      }
    }
  }

  private initializeIframeCommunication(): void {
    this.sendHeightToParent();

    window.addEventListener("resize", () => this.sendHeightToParent());

    const observer = new MutationObserver(() => {
      this.sendHeightToParent();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  private sendHeightToParent(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const height = document.body.scrollHeight;

        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              type: "RESIZE_IFRAME",
              height,
            },
            "*",
          );
        }

        window.postMessage(
          {
            type: "RESIZE_IFRAME",
            height,
          },
          "*",
        );
      });
    });
  }
}
